import React from "react";
import Select from "react-select";
import { Button, Col, FormFeedback, Label, Row } from "reactstrap";

/**
 * sitemap building block (SCRUM-642, VariableField registry) — rows of
 * `{ category, subcategories }` where `category` is a FULL category doc from
 * redux and `subcategories` an array of FULL subcategory sub-docs (no
 * projection; the whole `selected.value` doc is stored, pinned by e2e).
 *
 * Byte-identical port of the three legacy surfaces (SCRUM-630/640 pinned):
 * - wizard IndexTemplateSelector.jsx 1339–1476
 *   (tests/integration/pages/contentVariables.types.test.jsx)
 * - UpdatePage/the former Required host (.jsx) 1044–1179
 *   (tests/integration/pages/handleContentRequired.composites.test.jsx)
 * - UpdatePage/the former Optional host (.jsx) 1547–1681
 *   (tests/integration/pages/handleContentOptional.types.test.jsx)
 *
 * Contract: emits the CANONICAL row array via `onChange(name, nextItems)`.
 * The per-surface reporting envelopes are HOST concerns (SCRUM-643 wiring):
 * - wizard:          onChangeVariableValue({ target: { name, value } })
 * - update Required: onVariableChange({ ...storedVar, value }) (whole object)
 * - update Optional: onChangeContentOptional({ target: { name, value } })
 *   and onVariableChange is NOT called (the only branch on that channel).
 *
 * Variant differences (`variant="create" | "update"`, default "update") —
 * the ONLY difference; all other DOM is byte-identical across surfaces:
 * - create (wizard): both Selects get `className={validationError ?
 *   'is-invalid' : ''}` (NOT gated on isValidationAttempted), plus the
 *   trailing FormFeedback Row which additionally needs
 *   `isValidationAttempted` — driven by the optional `validationError` /
 *   `isValidationAttempted` props.
 * - update: no validation chrome at all; the Selects get no className
 *   (passing `undefined` here is DOM-identical to the legacy absent prop).
 *
 * Data: `categories` prop = raw `state.categories.categories` slice. The
 * block builds `localCategories` (`{ label: cat.name, value: cat }`,
 * prepareCategories verbatim) and `getSubcategoriesForCategory` reads the
 * slice DIRECTLY by `_id` with plain `sub.name` labels (not the composite
 * labels of the shared prepareSubCategories). The getCategories fetch stays
 * a host concern; the block is redux-free.
 *
 * Preserved quirks:
 * - category change rebuilds the row as exactly `{ category,
 *   subcategories: [] }` (no `...item` spread → subcats reset, extra row
 *   keys dropped); clearing emits `{ category: null, subcategories: [] }`;
 *   subcategory change DOES spread (`{ ...item, subcategories }`).
 * - display-only fallback: non-array/empty value renders one blank row and
 *   emits nothing; the first interaction emits the fully normalized array
 *   ("+ Add" on a pristine `""` emits TWO rows: fallback blank + new blank).
 * - remove guarded: button `disabled={items.length === 1}` AND a
 *   `length > 1` guard in the handler; text `&minus; Remove` (U+2212).
 * - index-based row keys; removing row N re-keys the tail.
 * - block-owned header Row (variable-name Label + `+ Add Category Row`) —
 *   sitemap deviation: the host does NOT own the label. Always expanded, no
 *   <details>/collapse, no summaryProps (the wizard drag handle is host
 *   chrome outside the block).
 * - `varible_container` typo class (sic, CSS/e2e) and the Select `name`
 *   attrs `${variable}[${i}].category` / `.subcategories` (e2e locators).
 * - hydration display-loss: stored selections not resolvable against the
 *   current `categories` slice render blank (category select empty; stored
 *   subcats silently not displayed but kept in the value until the next
 *   subcategories edit of that row); subcat OPTIONS derive from the slice
 *   doc, never from the stored category snapshot.
 *
 * Named deviation (deliberate, degenerate-case only): options derive
 * synchronously from the `categories` prop each render, where the legacy
 * hosts cached them in a `length > 0`-guarded effect — so legacy briefly
 * rendered an empty dropdown pre-effect and kept STALE options if the slice
 * ever transitioned non-empty → empty (no code path does this today). A pure
 * prop-driven block does not reproduce that cache artifact; settled DOM is
 * identical for any stable non-empty slice.
 */
export type SitemapSubcategory = { _id?: string; name?: string };
export type SitemapCategory = {
  _id?: string;
  name?: string;
  subcategories?: SitemapSubcategory[];
};
/** One stored row: a full category doc and full subcategory sub-docs. */
export type SitemapItem = {
  category: SitemapCategory | null;
  subcategories?: SitemapSubcategory[];
};
type CategoryOption = { label?: string; value: SitemapCategory };
type SubcategoryOption = { label?: string; value: SitemapSubcategory };

export type SitemapFieldProps = {
  settingVar: { variable?: string; value?: unknown };
  onChange: (name: string | undefined, value: SitemapItem[]) => void;
  variant?: "create" | "update";
  categories?: SitemapCategory[];
  validationError?: React.ReactNode;
  isValidationAttempted?: boolean;
};

export default function SitemapField({
  settingVar,
  onChange,
  variant = "update",
  categories = [],
  validationError,
  isValidationAttempted = false,
}: SitemapFieldProps) {
  const fieldName = settingVar.variable;

  // Normalize value to array of { category: {...}, subcategories: [...] }
  const items: SitemapItem[] =
    Array.isArray(settingVar.value) && settingVar.value.length > 0
      ? settingVar.value
      : [{ category: null, subcategories: [] }];

  const setItems = (nextItems: SitemapItem[]) => {
    onChange(fieldName, nextItems);
  };

  const updateCategory = (idx: number) => (selected: CategoryOption | null) => {
    const newItems = items.map((item, i) =>
      i === idx
        ? { category: selected ? selected.value : null, subcategories: [] }
        : item
    );
    setItems(newItems);
  };

  const updateSubcategories = (idx: number) => (selected: readonly SubcategoryOption[] | null) => {
    const newItems = items.map((item, i) =>
      i === idx
        ? { ...item, subcategories: selected ? selected.map((s) => s.value) : [] }
        : item
    );
    setItems(newItems);
  };

  const addItem = () => {
    setItems([...items, { category: null, subcategories: [] }]);
  };

  const removeItem = (idx: number) => {
    if (items.length > 1) {
      setItems(items.filter((_, i) => i !== idx));
    }
  };

  const localCategories: CategoryOption[] = categories.map((cat) => ({
    label: cat.name,
    value: cat,
  }));

  // Get subcategories for a specific category
  const getSubcategoriesForCategory = (category: SitemapCategory | null): SubcategoryOption[] => {
    if (!category || !category._id) return [];
    const cat = categories.find((c) => c._id === category._id);
    if (!cat || !cat.subcategories) return [];
    return cat.subcategories.map((sub) => ({
      label: sub.name,
      value: sub,
    }));
  };

  // Wizard-only Select chrome; "" reproduces the legacy ternary's no-error arm.
  const invalidClass =
    variant === "create" ? (validationError ? "is-invalid" : "") : undefined;

  return (
    <div className="sitemap-container">
      <Row className="varible_container mb-3">
        <Label md="2" className="form-control-label">
          {fieldName}
        </Label>
        <Col md="10">
          <Button type="button" color="primary" size="sm" onClick={addItem}>
            + Add Category Row
          </Button>
        </Col>
      </Row>

      {items.map((item, i) => {
        const subcatOptions = getSubcategoriesForCategory(item.category);
        const storedSubcats = item.subcategories;
        const storedCategory = item.category;
        const selectedSubcats = storedSubcats
          ? subcatOptions.filter((opt) =>
              storedSubcats.some((sub) => sub._id === opt.value._id)
            )
          : [];

        return (
          <div
            // index key: controlled row, no row-local state (SCRUM-2183)
            key={i}
            className="sitemap-item mb-4 p-3"
            style={{ border: "1px solid #e9ecef", borderRadius: "8px", backgroundColor: "#f8f9fa" }}
          >
            <Row className="varible_container mb-3">
              <Label md="2" className="form-control-label">
                Category #{i + 1}
              </Label>
              <Col md="8">
                <Select
                  name={`${fieldName}[${i}].category`}
                  options={localCategories}
                  value={storedCategory ? localCategories.find((opt) => opt.value._id === storedCategory._id) : null}
                  onChange={updateCategory(i)}
                  isSearchable
                  isClearable
                  placeholder="Select a category..."
                  className={invalidClass}
                />
              </Col>
              <Col md="2" className="d-flex justify-content-end">
                <Button
                  type="button"
                  color="danger"
                  size="sm"
                  onClick={() => removeItem(i)}
                  disabled={items.length === 1}
                >
                  &minus; Remove
                </Button>
              </Col>
            </Row>

            <Row className="varible_container">
              <Label md="2" className="form-control-label">
                Subcategories
              </Label>
              <Col md="10">
                <Select
                  name={`${fieldName}[${i}].subcategories`}
                  options={subcatOptions}
                  value={selectedSubcats}
                  onChange={updateSubcategories(i)}
                  isSearchable
                  isMulti
                  isDisabled={!item.category}
                  placeholder={item.category ? "Select subcategories..." : "Select a category first..."}
                  className={invalidClass}
                />
              </Col>
            </Row>
          </div>
        );
      })}

      {variant === "create" && isValidationAttempted && validationError && (
        <Row className="mt-2">
          <Col md={{ offset: 2, size: 10 }}>
            <FormFeedback className="medium-text d-block">
              {validationError}
            </FormFeedback>
          </Col>
        </Row>
      )}
    </div>
  );
}

// renders its own <summary>/header chrome - the dispatcher skips its label row (SCRUM-643)
SitemapField.ownsChrome = true;
